/**
 * Resolves `blockId` + `screenId` из `location.pathname` для context Block 10
 * feedback (передаётся в `navigate('/feedback', { state })`).
 *
 * Маппинг адаптирован под реальные routes в `apps/web/src/router.tsx`
 * (см. JSDoc там для full breakdown). PRD §номера экранов: §5 Decor (block-2),
 * §6 Paint (block-3), §7 Handles (block-4), §8 Configurator (block-6),
 * §15 Calculator (block-12), §16 Contract (block-13).
 *
 * **Технический долг (B-085):** switch-case дублирует знание о routes
 * (нарушение SSOT с `router.tsx`). Чище — `useMatches()` с `route.handle`
 * metadata. Refactor — Phase 5+ Block 10 cleanup или при первом добавлении
 * нового блок-trigger feedback.
 *
 * **P-031 (anti-parsing):** blockId/screenId уезжают через React Router state
 * (`navigate(..., { state })`), а НЕ URL params — чтобы не раскрывать
 * структуру маршрутов в bookmarks/Sentry breadcrumbs/share-link.
 *
 * References: ADR-0018 §Q5 (blockId/screenId source — useLocation().state).
 */

export interface RouteContext {
  blockId: string;
  screenId: string;
}

const UNKNOWN: RouteContext = { blockId: 'unknown', screenId: 'unknown' };

export function inferRouteContext(pathname: string): RouteContext {
  // Order matters — more specific paths first.
  if (pathname === '/' || pathname === '') {
    return { blockId: 'block-0', screenId: '2.0' };
  }
  if (pathname.startsWith('/onboarding/')) {
    return { blockId: 'block-0', screenId: inferOnboardingScreenId(pathname) };
  }
  if (pathname === '/install' || pathname.startsWith('/install/')) {
    return { blockId: 'block-0', screenId: '2.0.PWA' };
  }
  if (pathname === '/feedback' || pathname.startsWith('/feedback/')) {
    return { blockId: 'block-10', screenId: pathname === '/feedback/sent' ? '2.10.2' : '2.10.1' };
  }
  if (pathname === '/decors' || pathname.startsWith('/decors/')) {
    return { blockId: 'block-2', screenId: inferDecorsScreenId(pathname) };
  }
  if (pathname === '/handles' || pathname.startsWith('/handles/')) {
    return { blockId: 'block-4', screenId: inferHandlesScreenId(pathname) };
  }
  if (pathname === '/paint' || pathname.startsWith('/paint/')) {
    return { blockId: 'block-3', screenId: inferPaintScreenId(pathname) };
  }
  if (pathname === '/configurator' || pathname.startsWith('/configurator/')) {
    return { blockId: 'block-6', screenId: inferConfiguratorScreenId(pathname) };
  }
  if (pathname === '/calculator' || pathname.startsWith('/calculator/')) {
    return { blockId: 'block-12', screenId: '2.12.1' };
  }
  if (pathname === '/contracts' || pathname.startsWith('/contracts/')) {
    // Block 13 PR-G Phase 6 — real routes wired. Map:
    //   /contracts        → 2.13.1 (Мои договоры)
    //   /contracts/new    → 2.13.2 (Создание)
    //   /contracts/:id/edit → 2.13.2 (Редактирование)
    //   /contracts/:id/pdf  → 2.13.3 (Готовый PDF)
    return { blockId: 'block-13', screenId: inferContractsScreenId(pathname) };
  }
  return UNKNOWN;
}

function inferContractsScreenId(pathname: string): string {
  if (pathname === '/contracts') return '2.13.1';
  if (pathname === '/contracts/new') return '2.13.2';
  if (pathname.endsWith('/pdf')) return '2.13.3';
  if (pathname.endsWith('/edit')) return '2.13.2';
  return '2.13.0';
}

// /onboarding/lang-city → 2.0.1, /onboarding/register → 2.0.2.
function inferOnboardingScreenId(pathname: string): string {
  if (pathname === '/onboarding/lang-city') return '2.0.1';
  if (pathname === '/onboarding/register') return '2.0.2';
  return 'unknown';
}

// /decors → 2.2.1 (KindPicker), /decors/:kind → 2.2.1.1 (фильтр-экран, PR-FLOW),
// /decors/:kind/method → 2.2.2 (метод-пикер), /decors/:kind/catalog → 2.2.6,
// /decors/:kind/photo → 2.2.3, /decors/:kind/:globalId → 2.2.7,
// /.../alternatives → 2.2.8, /.../shops → 2.2.9.
function inferDecorsScreenId(pathname: string): string {
  if (pathname === '/decors') return '2.2.1';
  const parts = pathname.split('/').filter(Boolean); // ['decors', ':kind', ...]
  // /decors/:kind — фильтр-экран (сразу после KindPicker, ДО метод-пикера).
  if (parts.length === 2) return '2.2.1.1';
  // /decors/:kind/method (проверка ДО голого length===3)
  if (parts.length === 3 && parts[2] === 'method') return '2.2.2';
  // /decors/:kind/catalog (проверка ДО голого length===3)
  if (parts.length === 3 && parts[2] === 'catalog') return '2.2.6';
  // /decors/:kind/photo (проверка ДО голого length===3)
  if (parts.length === 3 && parts[2] === 'photo') return '2.2.3';
  // /decors/:kind/:globalId
  if (parts.length === 3) return '2.2.7';
  // /decors/:kind/:globalId/alternatives
  if (parts.length === 4 && parts[3] === 'alternatives') return '2.2.8';
  // /decors/:kind/:globalId/shops
  if (parts.length === 4 && parts[3] === 'shops') return '2.2.9';
  return 'unknown';
}

// /handles → 2.4.1, /handles/:kind → 2.4.2, /handles/:kind/:globalId → 2.4.3,
// /.../shops → 2.4.4.
function inferHandlesScreenId(pathname: string): string {
  if (pathname === '/handles') return '2.4.1';
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 2) return '2.4.2';
  if (parts.length === 3) return '2.4.3';
  if (parts.length === 4 && parts[3] === 'shops') return '2.4.4';
  return 'unknown';
}

// /paint → 2.3.1, /paint/photo → 2.3.2, /paint/:globalId → 2.3.7,
// /.../alternatives → 2.3.8, /.../shops → 2.3.9.
function inferPaintScreenId(pathname: string): string {
  if (pathname === '/paint') return '2.3.1';
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 2 && parts[1] === 'photo') return '2.3.2';
  if (parts.length === 2) return '2.3.7';
  if (parts.length === 3 && parts[2] === 'alternatives') return '2.3.8';
  if (parts.length === 3 && parts[2] === 'shops') return '2.3.9';
  return 'unknown';
}

// /configurator → 2.6.1 (TypePicker), /configurator/:type → 2.6.2.
// Deeper paths fallback to 'unknown' for symmetry с sibling inferrers.
function inferConfiguratorScreenId(pathname: string): string {
  if (pathname === '/configurator') return '2.6.1';
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 2) return '2.6.2';
  return 'unknown';
}
